import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  getSpamHeroSettings,
  saveSpamHeroSettings,
  SpamHeroCheckResult,
} from '../../services/api';
import { useEmailHealth } from '../../providers/EmailHealthProvider';
import {
  AlertTriangle,
  XCircle,
  MinusCircle,
  RefreshCw,
  Settings,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Play,
  CheckCircle,
  Loader2,
  Save,
  Plus,
  Trash2,
  Mail,
} from 'lucide-react';
import { format, parseISO, formatDistanceToNow } from 'date-fns';

// ── Types ──────────────────────────────────────────────────────────────────

type SortDirection = 'asc' | 'desc';

interface SortConfig {
  column: string;
  direction: SortDirection;
}

interface ColumnFilters {
  clientName?: string;
  domain?: string;
  usesSpamHero?: string;
  mxRouting?: string;
  inboundConnector?: string;
  enhancedFiltering?: string;
  ipMatch?: string;
  overallStatus?: string;
  lastCheck?: string;
}

type FilterableColumn = keyof ColumnFilters;

// ── Sort Icon ──────────────────────────────────────────────────────────────

function SortIcon({ direction, order }: { direction?: SortDirection; order?: number }) {
  if (!direction) {
    return (
      <span className="ml-1 text-muted-foreground text-xs select-none" title="Unsorted">
        ○
      </span>
    );
  }
  return (
    <span className="ml-1 inline-flex items-center text-blue-500 dark:text-blue-400 text-xs select-none font-bold">
      {direction === 'asc' ? '▲' : '▼'}
      {order !== undefined && order > 0 && (
        <span className="ml-0.5 text-[10px] bg-blue-500 text-white rounded-full w-3.5 h-3.5 inline-flex items-center justify-center leading-none">
          {order}
        </span>
      )}
    </span>
  );
}

// ── Filter Icon ────────────────────────────────────────────────────────────

function FilterIcon({ active }: { active: boolean }) {
  return (
    <svg
      className={`w-3 h-3 ml-1 ${active ? 'text-blue-500 dark:text-blue-400' : 'text-muted-foreground'}`}
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path fillRule="evenodd" d="M3 3a1 1 0 011-1h12a1 1 0 011 1v3a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V3z" clipRule="evenodd" />
    </svg>
  );
}

// ── Filter Popup Portal ────────────────────────────────────────────────────

function FilterPopupPortal({ anchorRef, children, onClose }: {
  anchorRef: React.RefObject<HTMLElement | null>;
  children: React.ReactNode;
  onClose: () => void;
}) {
  const popupRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (anchorRef.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPos({
        top: rect.bottom + 4,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - 260)),
      });
    }
  }, [anchorRef]);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (popupRef.current && !popupRef.current.contains(e.target as Node) &&
          anchorRef.current && !anchorRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose, anchorRef]);

  return createPortal(
    <div
      ref={popupRef}
      className="fixed z-[9999] bg-card border border-border rounded-lg shadow-xl min-w-[220px] max-w-[280px]"
      style={{ top: pos.top, left: pos.left }}
    >
      {children}
    </div>,
    document.body
  );
}

// ── Text Filter Popup ──────────────────────────────────────────────────────

function TextFilterPopup({ value, suggestions, onChange, onClose }: {
  value: string;
  suggestions: string[];
  onChange: (v: string) => void;
  onClose: () => void;
}) {
  const [input, setInput] = useState(value);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const filtered = useMemo(() => {
    if (!input) return suggestions.slice(0, 15);
    const lower = input.toLowerCase();
    return suggestions.filter(s => s.toLowerCase().includes(lower)).slice(0, 15);
  }, [input, suggestions]);

  const apply = (val: string) => {
    onChange(val);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIdx >= 0 && highlightIdx < filtered.length) {
        apply(filtered[highlightIdx]);
      } else {
        apply(input);
      }
    }
  };

  return (
    <div className="p-3">
      <input
        ref={inputRef}
        type="text"
        value={input}
        onChange={e => { setInput(e.target.value); setHighlightIdx(-1); }}
        onKeyDown={handleKeyDown}
        placeholder="Type to filter..."
        className="w-full px-2 py-1.5 text-sm border border-border rounded bg-card text-foreground focus:outline-none focus:ring-1 focus:ring-blue-500"
      />
      {filtered.length > 0 && (
        <ul className="mt-2 max-h-40 overflow-y-auto text-sm">
          {filtered.map((s, i) => (
            <li
              key={s}
              className={`px-2 py-1 rounded cursor-pointer ${i === highlightIdx ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300' : 'hover:bg-accent text-foreground'}`}
              onMouseEnter={() => setHighlightIdx(i)}
              onClick={() => apply(s)}
            >
              {s}
            </li>
          ))}
        </ul>
      )}
      <div className="mt-2 flex justify-between border-t border-border pt-2">
        <button
          onClick={() => { onChange(''); onClose(); }}
          className="text-xs text-muted-foreground hover:text-foreground"
        >
          Clear
        </button>
        <button
          onClick={() => apply(input)}
          className="text-xs text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-200 font-medium"
        >
          Done
        </button>
      </div>
    </div>
  );
}

// ── Dropdown Filter Popup ──────────────────────────────────────────────────

function DropdownFilterPopup({ value, options, onChange, onClose }: {
  value: string;
  options: { label: string; value: string }[];
  onChange: (v: string) => void;
  onClose: () => void;
}) {
  const select = (v: string) => {
    onChange(v);
    onClose();
  };

  return (
    <div className="py-1">
      <button
        onClick={() => select('')}
        className={`w-full text-left px-3 py-1.5 text-sm ${!value ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium' : 'text-foreground hover:bg-accent'}`}
      >
        All
      </button>
      {options.map(opt => (
        <button
          key={opt.value}
          onClick={() => select(opt.value)}
          className={`w-full text-left px-3 py-1.5 text-sm ${value === opt.value ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-medium' : 'text-foreground hover:bg-accent'}`}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Status helpers ─────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: SpamHeroCheckResult['overallStatus'] }) {
  const config = {
    healthy: { icon: CheckCircle, label: 'Healthy', className: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400' },
    issues: { icon: AlertTriangle, label: 'Issues', className: 'bg-amber-500/10 text-amber-600 dark:text-amber-400' },
    critical: { icon: XCircle, label: 'Critical', className: 'bg-red-500/10 text-red-600 dark:text-red-400' },
    'n/a': { icon: MinusCircle, label: 'N/A', className: 'bg-muted text-muted-foreground' },
  };
  const c = config[status];
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.className}`}>
      <c.icon className="h-3.5 w-3.5" />
      {c.label}
    </span>
  );
}

function CheckStatusIcon({ status }: { status: string }) {
  if (status === 'pass') return <CheckCircle className="h-4 w-4 text-emerald-500" />;
  if (status === 'fail') return <XCircle className="h-4 w-4 text-red-500" />;
  if (status === 'warning') return <AlertTriangle className="h-4 w-4 text-amber-500" />;
  if (status === 'unknown') return <MinusCircle className="h-4 w-4 text-muted-foreground" />;
  return <MinusCircle className="h-4 w-4 text-muted-foreground" />;
}

// ── Check status filter options (shared across MX, Connector, EF, IPs) ───

const CHECK_STATUS_OPTIONS = [
  { label: 'Pass', value: 'pass' },
  { label: 'Fail', value: 'fail' },
  { label: 'Warning', value: 'warning' },
  { label: 'N/A', value: 'n/a' },
];

// ── Column definitions ─────────────────────────────────────────────────────

interface ColumnDef {
  key: string;
  header: string;
  tooltip?: string;
  align?: 'left' | 'center';
  sortable?: boolean;
  filterable?: FilterableColumn;
  filterType?: 'text' | 'dropdown';
  filterOptions?: { label: string; value: string }[];
}

const COLUMN_DEFS: ColumnDef[] = [
  { key: 'clientName', header: 'Client', align: 'left', sortable: true, filterable: 'clientName', filterType: 'text' },
  { key: 'domain', header: 'Domain', align: 'left', sortable: true, filterable: 'domain', filterType: 'text' },
  {
    key: 'usesSpamHero', header: 'SpamHero?', align: 'center', sortable: true,
    filterable: 'usesSpamHero', filterType: 'dropdown',
    filterOptions: [{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }],
  },
  { key: 'mxRouting', header: 'MX', tooltip: 'MX Routing — Mail exchange records point to SpamHero', align: 'center', sortable: true, filterable: 'mxRouting', filterType: 'dropdown', filterOptions: CHECK_STATUS_OPTIONS },
  { key: 'inboundConnector', header: 'Connector', tooltip: 'Inbound Connector — Exchange connector configured for SpamHero', align: 'center', sortable: true, filterable: 'inboundConnector', filterType: 'dropdown', filterOptions: CHECK_STATUS_OPTIONS },
  { key: 'enhancedFiltering', header: 'EF', tooltip: 'Enhanced Filtering — Skip-listing enabled so Exchange sees original sender IP', align: 'center', sortable: true, filterable: 'enhancedFiltering', filterType: 'dropdown', filterOptions: CHECK_STATUS_OPTIONS },
  { key: 'ipMatch', header: 'IPs', tooltip: 'IP Match — SpamHero IPs match the known-good list in Settings', align: 'center', sortable: true, filterable: 'ipMatch', filterType: 'dropdown', filterOptions: CHECK_STATUS_OPTIONS },
  {
    key: 'overallStatus', header: 'Status', align: 'center', sortable: true,
    filterable: 'overallStatus', filterType: 'dropdown',
    filterOptions: [
      { label: 'Healthy', value: 'healthy' },
      { label: 'Issues', value: 'issues' },
      { label: 'Critical', value: 'critical' },
      { label: 'N/A', value: 'n/a' },
    ],
  },
  {
    key: 'lastCheck', header: 'Last Check', align: 'center', sortable: true,
    filterable: 'lastCheck', filterType: 'dropdown',
    filterOptions: [
      { label: 'Recent (<48h)', value: 'recent' },
      { label: 'Stale (>48h)', value: 'stale' },
      { label: 'Never', value: 'never' },
    ],
  },
  { key: 'action', header: 'Action', align: 'center' },
];

// ── Main Component ─────────────────────────────────────────────────────────

export default function EmailHealthDashboard() {
  const {
    runAllLoading,
    runAllProgress,
    runningClients,
    checks,
    summary,
    loading,
    error,
    startRunAll,
    runSingleCheck,
    fetchData,
  } = useEmailHealth();

  // Sort state — default: client name ascending
  const [sorts, setSorts] = useState<SortConfig[]>([{ column: 'clientName', direction: 'asc' }]);

  // Filter state
  const [columnFilters, setColumnFilters] = useState<ColumnFilters>({});

  // Which filter popup is open
  const [openFilter, setOpenFilter] = useState<string | null>(null);

  // Refs for filter icon anchors
  const filterRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Pagination
  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);

  // Settings panel (local to this page)
  const [showSettings, setShowSettings] = useState(false);
  const [settingsIps, setSettingsIps] = useState<string[]>([]);
  const [newIp, setNewIp] = useState('');
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const data = await getSpamHeroSettings();
      setSettingsIps(data.settings.knownGoodIps || []);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (checks.length === 0 && !loading) {
      fetchData();
    }
    fetchSettings();
  }, [fetchData, fetchSettings, checks.length, loading]);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [columnFilters]);

  // ── Sort logic ─────────────────────────────────────────────────────────

  const handleSort = (column: string, addToMulti: boolean) => {
    setSorts(prev => {
      const existingIdx = prev.findIndex(s => s.column === column);
      if (addToMulti) {
        if (existingIdx >= 0) {
          const existing = prev[existingIdx];
          if (existing.direction === 'asc') {
            const next = [...prev];
            next[existingIdx] = { column, direction: 'desc' };
            return next;
          } else {
            return prev.filter((_, i) => i !== existingIdx);
          }
        } else {
          return [...prev, { column, direction: 'asc' }];
        }
      } else {
        if (existingIdx >= 0 && prev.length === 1) {
          const existing = prev[existingIdx];
          if (existing.direction === 'asc') return [{ column, direction: 'desc' }];
          return [];
        }
        return [{ column, direction: 'asc' }];
      }
    });
    setPage(1);
  };

  function getSortValue(check: SpamHeroCheckResult, column: string): string {
    switch (column) {
      case 'clientName': return (check.clientName || '').trim().toLowerCase();
      case 'domain': return (check.domain || '').toLowerCase();
      case 'usesSpamHero': return check.usesSpamHero ? '0' : '1';
      case 'mxRouting': return check.checks.mxRouting.status;
      case 'inboundConnector': return check.checks.inboundConnector.status;
      case 'enhancedFiltering': return check.checks.enhancedFiltering.status;
      case 'ipMatch': return check.checks.ipMatch.status;
      case 'overallStatus': return check.overallStatus;
      case 'lastCheck': return check.checkedAt || '';
      default: return '';
    }
  }

  // ── Filter + Sort + Paginate ───────────────────────────────────────────

  const filtered = checks.filter(check => {
    if (columnFilters.clientName) {
      if (!check.clientName.toLowerCase().includes(columnFilters.clientName.toLowerCase())) return false;
    }
    if (columnFilters.domain) {
      if (!(check.domain || '').toLowerCase().includes(columnFilters.domain.toLowerCase())) return false;
    }
    if (columnFilters.usesSpamHero) {
      if (columnFilters.usesSpamHero === 'yes' && !check.usesSpamHero) return false;
      if (columnFilters.usesSpamHero === 'no' && check.usesSpamHero) return false;
    }
    if (columnFilters.mxRouting && check.checks.mxRouting.status !== columnFilters.mxRouting) return false;
    if (columnFilters.inboundConnector && check.checks.inboundConnector.status !== columnFilters.inboundConnector) return false;
    if (columnFilters.enhancedFiltering && check.checks.enhancedFiltering.status !== columnFilters.enhancedFiltering) return false;
    if (columnFilters.ipMatch && check.checks.ipMatch.status !== columnFilters.ipMatch) return false;
    if (columnFilters.overallStatus && check.overallStatus !== columnFilters.overallStatus) return false;
    if (columnFilters.lastCheck) {
      const ts = check.checkedAt;
      if (columnFilters.lastCheck === 'never') { if (ts) return false; }
      else if (!ts) return false;
      else {
        const age = Date.now() - new Date(ts).getTime();
        const threshold = 48 * 60 * 60 * 1000;
        if (columnFilters.lastCheck === 'recent' && age >= threshold) return false;
        if (columnFilters.lastCheck === 'stale' && age < threshold) return false;
      }
    }
    return true;
  });

  // Sort (multi-column)
  const sorted = sorts.length > 0
    ? [...filtered].sort((a, b) => {
        for (const sort of sorts) {
          const aVal = getSortValue(a, sort.column);
          const bVal = getSortValue(b, sort.column);
          const cmp = aVal.localeCompare(bVal, undefined, { sensitivity: 'base' });
          if (cmp !== 0) return sort.direction === 'asc' ? cmp : -cmp;
        }
        return 0;
      })
    : filtered;

  // Pagination
  const totalFiltered = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedChecks = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Suggestions for text filters
  const clientNames = useMemo(
    () => [...new Set(checks.map(c => c.clientName))].sort((a, b) => a.localeCompare(b)),
    [checks]
  );
  const domains = useMemo(
    () => [...new Set(checks.map(c => c.domain).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [checks]
  );

  const getSuggestions = (col: string) => col === 'clientName' ? clientNames : domains;

  // Active filter count
  const activeFilterCount = Object.values(columnFilters).filter(Boolean).length;

  // ── Sort/filter render helpers ────────────────────────────────────────

  const getSortInfo = (column: string) => {
    const idx = sorts.findIndex(s => s.column === column);
    if (idx < 0) return { direction: undefined, order: undefined };
    return { direction: sorts[idx].direction, order: sorts.length > 1 ? idx + 1 : undefined };
  };

  const setFilter = (key: FilterableColumn, value: string) => {
    setColumnFilters(prev => ({ ...prev, [key]: value || undefined }));
  };

  // ── Settings handlers ────────────────────────────────────────────────

  const handleSaveSettings = async () => {
    setSettingsLoading(true);
    try {
      await saveSpamHeroSettings(settingsIps);
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch {
      // error
    } finally {
      setSettingsLoading(false);
    }
  };

  const addIp = () => {
    const ip = newIp.trim();
    if (ip && !settingsIps.includes(ip)) {
      setSettingsIps([...settingsIps, ip]);
      setNewIp('');
    }
  };

  const removeIp = (ip: string) => {
    setSettingsIps(settingsIps.filter(i => i !== ip));
  };

  // ── Loading state ────────────────────────────────────────────────────

  if (loading && checks.length === 0) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Mail className="h-6 w-6 text-primary-600 dark:text-primary-400" />
          <h1 className="text-2xl font-bold text-foreground">Email Health</h1>
        </div>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Mail className="h-6 w-6 text-primary-600 dark:text-primary-400" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Email Health</h1>
            <p className="text-sm text-muted-foreground">SpamHero connector health check across all client tenants</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowSettings(!showSettings)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-border text-foreground hover:bg-accent transition-colors"
          >
            <Settings className="h-4 w-4" />
            Settings
          </button>
          <button
            onClick={startRunAll}
            disabled={runAllLoading}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50"
          >
            {runAllLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
            Run All
          </button>
          <button
            onClick={fetchData}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border border-border text-foreground hover:bg-accent transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-4 text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Run All Progress */}
      {runAllProgress && (
        <div className="rounded-xl border border-primary-500/30 bg-primary-500/5 p-4 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-foreground font-medium">
              Checking: <span className="text-primary-600 dark:text-primary-400">{runAllProgress.clientName}</span>
            </span>
            <span className="text-muted-foreground">{runAllProgress.current} / {runAllProgress.total}</span>
          </div>
          <div className="w-full bg-muted rounded-full h-2">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(runAllProgress.current / runAllProgress.total) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Settings Panel */}
      {showSettings && (
        <div className="rounded-xl border border-border bg-card p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">SpamHero Known-Good IPs</h3>
            <button onClick={() => setShowSettings(false)} className="text-muted-foreground hover:text-foreground">
              <ChevronUp className="h-5 w-5" />
            </button>
          </div>
          <p className="text-sm text-muted-foreground">
            These IPs are used to match inbound connectors to SpamHero. Add all SpamHero relay IPs that your clients use.
          </p>
          <div className="flex flex-wrap gap-2">
            {settingsIps.map(ip => (
              <span key={ip} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted text-sm font-mono">
                {ip}
                <button onClick={() => removeIp(ip)} className="text-muted-foreground hover:text-red-500">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={newIp}
              onChange={e => setNewIp(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addIp()}
              placeholder="e.g. 52.0.67.145"
              className="flex-1 px-3 py-2 rounded-lg border border-border bg-background text-sm font-mono text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
            <button onClick={addIp} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-sm hover:bg-accent">
              <Plus className="h-4 w-4" /> Add
            </button>
          </div>
          <div className="flex justify-end gap-2">
            {settingsSaved && <span className="text-sm text-emerald-600 dark:text-emerald-400 self-center">Saved!</span>}
            <button
              onClick={handleSaveSettings}
              disabled={settingsLoading}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {settingsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save IPs
            </button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="rounded-xl border border-border bg-card p-4">
            <div className="text-2xl font-bold text-foreground">{summary.spamHeroClients}</div>
            <div className="text-sm text-muted-foreground">SpamHero Clients</div>
          </div>
          <div
            className={`rounded-xl border p-4 cursor-pointer transition-colors ${columnFilters.overallStatus === 'healthy' ? 'border-emerald-500 bg-emerald-500/5' : 'border-border bg-card hover:border-emerald-500/50'}`}
            onClick={() => setFilter('overallStatus', columnFilters.overallStatus === 'healthy' ? '' : 'healthy')}
          >
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{summary.healthy}</div>
            <div className="text-sm text-muted-foreground">Healthy</div>
          </div>
          <div
            className={`rounded-xl border p-4 cursor-pointer transition-colors ${columnFilters.overallStatus === 'issues' ? 'border-amber-500 bg-amber-500/5' : 'border-border bg-card hover:border-amber-500/50'}`}
            onClick={() => setFilter('overallStatus', columnFilters.overallStatus === 'issues' ? '' : 'issues')}
          >
            <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">{summary.issues}</div>
            <div className="text-sm text-muted-foreground">Issues</div>
          </div>
          <div
            className={`rounded-xl border p-4 cursor-pointer transition-colors ${columnFilters.overallStatus === 'critical' ? 'border-red-500 bg-red-500/5' : 'border-border bg-card hover:border-red-500/50'}`}
            onClick={() => setFilter('overallStatus', columnFilters.overallStatus === 'critical' ? '' : 'critical')}
          >
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">{summary.critical}</div>
            <div className="text-sm text-muted-foreground">Critical</div>
          </div>
          <div
            className={`rounded-xl border p-4 cursor-pointer transition-colors ${columnFilters.overallStatus === 'n/a' ? 'border-border bg-muted' : 'border-border bg-card hover:border-border'}`}
            onClick={() => setFilter('overallStatus', columnFilters.overallStatus === 'n/a' ? '' : 'n/a')}
          >
            <div className="text-2xl font-bold text-muted-foreground">{summary.na}</div>
            <div className="text-sm text-muted-foreground">N/A</div>
          </div>
        </div>
      )}

      {/* Active filter badge */}
      {activeFilterCount > 0 && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">
            {totalFiltered} of {checks.length} checks shown
          </span>
          <button
            onClick={() => setColumnFilters({})}
            className="text-blue-600 dark:text-blue-400 hover:underline text-xs font-medium"
          >
            Clear all filters
          </button>
        </div>
      )}

      {/* Client Table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                {COLUMN_DEFS.map(col => {
                  const sortInfo = getSortInfo(col.key);
                  const filterActive = col.filterable ? !!columnFilters[col.filterable] : false;

                  return (
                    <th
                      key={col.key}
                      className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground ${col.align === 'center' ? 'text-center' : 'text-left'} ${col.sortable ? 'cursor-pointer select-none hover:bg-accent' : ''}`}
                    >
                      <div className={`flex items-center ${col.align === 'center' ? 'justify-center' : ''}`}>
                        <span
                          className="flex items-center"
                          title={col.tooltip}
                          onClick={col.sortable ? (e: React.MouseEvent) => {
                            e.stopPropagation();
                            handleSort(col.key, e.shiftKey);
                          } : undefined}
                        >
                          {col.tooltip ? (
                            <span className="border-b border-dotted border-muted-foreground/50 cursor-help">{col.header}</span>
                          ) : col.header}
                          {col.sortable && (
                            <SortIcon direction={sortInfo.direction} order={sortInfo.order} />
                          )}
                        </span>

                        {col.filterable && (
                          <button
                            ref={el => { filterRefs.current[col.key] = el; }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenFilter(prev => prev === col.key ? null : col.key);
                            }}
                            className="ml-1 p-0.5 rounded hover:bg-accent"
                            title={`Filter by ${col.header}`}
                          >
                            <FilterIcon active={filterActive} />
                          </button>
                        )}
                      </div>

                      {openFilter === col.key && col.filterable && (
                        <FilterPopupPortal
                          anchorRef={{ current: filterRefs.current[col.key] ?? null }}
                          onClose={() => setOpenFilter(null)}
                        >
                          {col.filterType === 'text' ? (
                            <TextFilterPopup
                              value={columnFilters[col.filterable] || ''}
                              suggestions={getSuggestions(col.key)}
                              onChange={v => setFilter(col.filterable!, v)}
                              onClose={() => setOpenFilter(null)}
                            />
                          ) : (
                            <DropdownFilterPopup
                              value={columnFilters[col.filterable] || ''}
                              options={col.filterOptions || []}
                              onChange={v => setFilter(col.filterable!, v)}
                              onClose={() => setOpenFilter(null)}
                            />
                          )}
                        </FilterPopupPortal>
                      )}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {pagedChecks.length === 0 && (
                <tr>
                  <td colSpan={COLUMN_DEFS.length} className="px-4 py-8 text-center text-muted-foreground">
                    {checks.length === 0 ? 'No health checks have been run yet. Click "Run All" to start.' : 'No checks match the current filters.'}
                  </td>
                </tr>
              )}
              {pagedChecks.map(check => (
                <tr key={check.clientId} className="hover:bg-muted/30 transition-colors">
                  <td className="px-4 py-3">
                    <a href={`/clients/${check.clientId}`} className="text-sm font-medium text-foreground hover:text-primary-600 dark:hover:text-primary-400">
                      {check.clientName}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground font-mono">{check.domain || '—'}</td>
                  <td className="px-4 py-3 text-center">
                    {check.usesSpamHero ? (
                      <span className="text-emerald-600 dark:text-emerald-400 text-sm font-medium">Yes</span>
                    ) : (
                      <span className="text-muted-foreground text-sm">No</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center"><CheckStatusIcon status={check.checks.mxRouting.status} /></td>
                  <td className="px-4 py-3 text-center"><CheckStatusIcon status={check.checks.inboundConnector.status} /></td>
                  <td className="px-4 py-3 text-center"><CheckStatusIcon status={check.checks.enhancedFiltering.status} /></td>
                  <td className="px-4 py-3 text-center"><CheckStatusIcon status={check.checks.ipMatch.status} /></td>
                  <td className="px-4 py-3 text-center"><StatusBadge status={check.overallStatus} /></td>
                  <td className="px-4 py-3 text-center text-xs text-muted-foreground">
                    {check.checkedAt ? (
                      <span title={format(parseISO(check.checkedAt), 'MMM d, yyyy h:mm a')}>
                        {formatDistanceToNow(parseISO(check.checkedAt), { addSuffix: true })}
                      </span>
                    ) : '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <button
                      onClick={() => runSingleCheck(check.clientId)}
                      disabled={runningClients.has(check.clientId) || runAllLoading}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-border hover:bg-accent disabled:opacity-50 transition-colors"
                    >
                      {runningClients.has(check.clientId)
                        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        : <RefreshCw className="h-3.5 w-3.5" />}
                      Check
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-card px-4 py-3 flex items-center justify-between border-t border-border sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={currentPage <= 1} className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-accent disabled:opacity-50">Previous</button>
              <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={currentPage >= totalPages} className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-accent disabled:opacity-50">Next</button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Showing{' '}
                <span className="font-medium">{(currentPage - 1) * PAGE_SIZE + 1}</span>{' '}
                to{' '}
                <span className="font-medium">{Math.min(currentPage * PAGE_SIZE, totalFiltered)}</span>{' '}
                of <span className="font-medium">{totalFiltered}</span> results
              </p>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-border bg-card text-sm font-medium text-muted-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <span className="relative inline-flex items-center px-4 py-2 border border-border bg-card text-sm font-medium text-foreground">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-border bg-card text-sm font-medium text-muted-foreground hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </nav>
            </div>
          </div>
        )}
      </div>

      {/* Hint */}
      <p className="text-xs text-muted-foreground text-center">
        Click column to sort &middot; Shift+click for multi-sort
      </p>

    </div>
  );
}
